// Form admin pra criar/editar External Plan.
// - saas_product_id imutável (select disabled em edit)
// - slug editável (admin pode renomear; backend valida colisão)

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  createExternalPlanSchema,
  type CreateExternalPlanDto,
  type CreateExternalPlanInput,
  type ExternalPlanStatusSchema,
} from '@control-center/shared';
import { useEffect } from 'react';
import { toast } from 'sonner';

import { Field, Input, Select, Textarea } from '../clients/form-primitives';
import { PillButton } from '../clients/PillButton';
import { firstErrorMessage } from '../../lib/form-errors';
import type { SaasProductEntity } from '../../lib/saas-products-api';

export interface ExternalPlanFormProps {
  defaultValues?: Partial<CreateExternalPlanInput>;
  editMode?: boolean;
  isPending?: boolean;
  saasProducts: SaasProductEntity[];
  onSubmit: (values: CreateExternalPlanDto) => void | Promise<void>;
  onCancel?: () => void;
  submitLabel?: string;
}

const STATUS_OPTIONS: { value: ExternalPlanStatusSchema; label: string }[] = [
  { value: 'ativo', label: 'Ativo' },
  { value: 'oculto', label: 'Oculto' },
  { value: 'legado', label: 'Legado' },
  { value: 'descontinuado', label: 'Descontinuado' },
];

const EMPTY: CreateExternalPlanInput = {
  saas_product_id: '',
  slug: '',
  name: '',
  description: '',
  status: 'ativo',
  metadata: {},
};

export function ExternalPlanForm({
  defaultValues,
  editMode = false,
  isPending = false,
  saasProducts,
  onSubmit,
  onCancel,
  submitLabel = 'Salvar',
}: ExternalPlanFormProps): JSX.Element {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateExternalPlanInput>({
    resolver: zodResolver(createExternalPlanSchema),
    defaultValues: { ...EMPTY, ...defaultValues },
    mode: 'onBlur',
  });

  useEffect(() => {
    if (defaultValues) reset({ ...EMPTY, ...defaultValues });
  }, [defaultValues, reset]);

  function submit(values: CreateExternalPlanInput): void | Promise<void> {
    const parsed = createExternalPlanSchema.parse(values);
    return onSubmit(parsed);
  }

  return (
    <form
      onSubmit={(e) =>
        void handleSubmit(submit, (errs) =>
          toast.error(firstErrorMessage(errs) ?? 'Formulário tem campos inválidos.'),
        )(e)
      }
      className="flex flex-col gap-4"
      noValidate
    >
      <Field label="SaaS Product" required error={errors.saas_product_id?.message}>
        {({ inputId, describedBy }) => (
          <Select
            id={inputId}
            aria-describedby={describedBy}
            disabled={editMode}
            {...register('saas_product_id')}
          >
            <option value="" disabled>
              Selecione o SaaS…
            </option>
            {saasProducts.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} ({s.slug})
              </option>
            ))}
          </Select>
        )}
      </Field>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Field
          label="Slug"
          required
          hint="Identificador do plano (lowercase, hífen, underscore)."
          error={errors.slug?.message}
        >
          {({ inputId, describedBy }) => (
            <Input
              id={inputId}
              aria-describedby={describedBy}
              placeholder="growth"
              {...register('slug')}
            />
          )}
        </Field>

        <Field label="Status" required error={errors.status?.message}>
          {({ inputId, describedBy }) => (
            <Select id={inputId} aria-describedby={describedBy} {...register('status')}>
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          )}
        </Field>
      </div>

      <Field label="Nome" required error={errors.name?.message}>
        {({ inputId, describedBy }) => (
          <Input
            id={inputId}
            aria-describedby={describedBy}
            placeholder="Growth"
            {...register('name')}
          />
        )}
      </Field>

      <Field
        label="Descrição"
        hint="Opcional, até 1000 caracteres."
        error={errors.description?.message}
      >
        {({ inputId, describedBy }) => (
          <Textarea
            id={inputId}
            aria-describedby={describedBy}
            rows={3}
            placeholder="Detalhes do plano…"
            {...register('description')}
          />
        )}
      </Field>

      <div className="flex items-center justify-end gap-2 border-t border-line pt-4">
        {onCancel && (
          <PillButton onClick={onCancel} type="button">
            Cancelar
          </PillButton>
        )}
        <PillButton solid type="submit" disabled={isPending}>
          {isPending ? 'Salvando…' : submitLabel}
        </PillButton>
      </div>
    </form>
  );
}
